import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccountCircle,
  Logout,
  ChevronLeft,
  ChevronRight,
  Lock,
  Check,
  Language,
  Receipt,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { NotificationBell } from '../NotificationBell';
import { ChangePasswordModal } from '../ChangePasswordModal';
import { CartButton } from '../cart/CartButton';
import { UserRole } from '@lysi-web/shared-types';
import { roleHome } from '../../lib/roleHome';

interface TopBarProps {
  onMenuClick: () => void;
  onSidebarToggle: () => void;
  sidebarCollapsed: boolean;
}

export const TopBar: React.FC<TopBarProps> = ({
  onMenuClick: _onMenuClick,
  onSidebarToggle,
  sidebarCollapsed,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, refetchUser } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const { t, i18n } = useTranslation();

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  const handleLogoClick = () => {
    if (!user) return;
    navigate(roleHome(user.role));
  };

  const handleChangePassword = () => {
    handleUserMenuClose();
    setShowPasswordModal(true);
  };

  const handlePasswordChangeSuccess = async () => {
    setShowPasswordModal(false);
    await refetchUser();
  };

  // Generate breadcrumbs from current path
  const generateBreadcrumbs = () => {
    // Drop UUID-shaped segments so detail routes like /admin/users/<uuid>
    // don't show the raw id as a breadcrumb crumb.
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const pathnames = location.pathname.split('/').filter((x) => x && !UUID_RE.test(x));

    // Map route segments to translation keys
    const nameKeyMap: Record<string, string> = {
      admin: 'breadcrumb.admin',
      doctor: 'breadcrumb.doctor',
      dashboard: 'breadcrumb.dashboard',
      users: 'breadcrumb.users',
      create: 'breadcrumb.create',
      specialties: 'breadcrumb.specialties',
      'insurance-companies': 'breadcrumb.insuranceCompanies',
      reports: 'breadcrumb.reports',
      orders: 'breadcrumb.prescriptions',
      templates: 'breadcrumb.templates',
      patients: 'breadcrumb.patients',
      medications: 'breadcrumb.medications',
      profile: 'breadcrumb.profile',
      catalog: 'breadcrumb.catalog',
      'pharmaceutical-labs': 'breadcrumb.pharmaceuticalLabs',
      'pharmacy-chains': 'breadcrumb.pharmacyChains',
      new: 'breadcrumb.new',
      'buscar':       'breadcrumb.search',
      'mis-pedidos':  'breadcrumb.myOrders',
      'checkout':     'breadcrumb.checkout',
    };

    return pathnames.map((segment, index) => {
      const path = `/${pathnames.slice(0, index + 1).join('/')}`;
      const isLast = index === pathnames.length - 1;
      const nameKey = nameKeyMap[segment];
      const name = nameKey ? t(nameKey) : segment;

      return {
        name,
        path,
        isLast,
      };
    });
  };

  const breadcrumbs = generateBreadcrumbs();

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return '?';
    if (user.role === 'DOCTOR' && user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user.email?.[0]?.toUpperCase() || '?';
  };

  // Get display name
  const getDisplayName = () => {
    if (!user) return 'User';
    if (user.role === 'DOCTOR' && user.firstName && user.lastName) {
      return `Dr. ${user.firstName} ${user.lastName}`;
    }
    return user.email || 'User';
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        top: 0,
      }}
    >
      <Toolbar sx={{ minHeight: 64 }}>
        {/* Desktop Sidebar Toggle */}
        {!isMobile && user?.role !== UserRole.CUSTOMER && (
          <IconButton
            color="inherit"
            aria-label="toggle sidebar"
            edge="start"
            onClick={onSidebarToggle}
            sx={{ mr: 2 }}
          >
            {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        )}

        {/* Brand / Home */}
        <Box
          onClick={handleLogoClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: user ? 'pointer' : 'default',
            mr: 2,
            userSelect: 'none',
            '&:hover': user ? { opacity: 0.8 } : undefined,
          }}
          aria-label="Ir al inicio"
          role={user ? 'button' : undefined}
        >
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{ color: '#0066CC', letterSpacing: 0.5 }}
          >
            Lysi
          </Typography>
        </Box>

        {/* Current page name */}
        <Box sx={{ flexGrow: 1 }}>
          {breadcrumbs.length > 0 && (
            <Typography variant="h6" fontWeight={600}>
              {breadcrumbs[breadcrumbs.length - 1]?.name}
            </Typography>
          )}
        </Box>

        {/* Right side items */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Cart Button - Customer only */}
          {user?.role === UserRole.CUSTOMER && <CartButton />}

          {/* Notifications */}
          <NotificationBell />

          {/* User Menu */}
          <Box
            onClick={handleUserMenuOpen}
            aria-controls={anchorEl ? 'user-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={anchorEl ? 'true' : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ml: 1,
              cursor: 'pointer',
              borderRadius: '10px',
              px: 1,
              py: 0.5,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
            }}
          >
            {/* Avatar with role badge */}
            <Box sx={{ position: 'relative' }}>
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: '#0D1B35',
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {getUserInitials()}
              </Avatar>
            </Box>

            {/* Display name — hidden on small screens */}
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: 'text.primary',
                display: { xs: 'none', sm: 'block' },
                maxWidth: 160,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {getDisplayName()}
            </Typography>
          </Box>

          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            onClick={handleUserMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1.5,
                minWidth: 220,
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
          >
            {/* User Info */}
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {getDisplayName()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>

            <Divider />

            {/* Profile (if doctor) */}
            {user?.role === 'DOCTOR' && (
              <MenuItem onClick={() => { handleUserMenuClose(); navigate('/doctor/profile'); }}>
                <ListItemIcon>
                  <AccountCircle fontSize="small" />
                </ListItemIcon>
                {t('auth.profile')}
              </MenuItem>
            )}

            {/* Mi perfil (if customer) */}
            {user?.role === UserRole.CUSTOMER && (
              <MenuItem onClick={() => { handleUserMenuClose(); navigate('/perfil'); }}>
                <ListItemIcon>
                  <AccountCircle fontSize="small" />
                </ListItemIcon>
                Mi perfil
              </MenuItem>
            )}

            {/* Mis pedidos (if customer) */}
            {user?.role === UserRole.CUSTOMER && (
              <MenuItem onClick={() => { handleUserMenuClose(); navigate('/mis-pedidos'); }}>
                <ListItemIcon>
                  <Receipt fontSize="small" />
                </ListItemIcon>
                Mis pedidos
              </MenuItem>
            )}

            {/* Change Password */}
            <MenuItem onClick={handleChangePassword}>
              <ListItemIcon>
                <Lock fontSize="small" />
              </ListItemIcon>
              {t('auth.changePassword')}
            </MenuItem>

            <Divider />

            {/* Language */}
            <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Language sx={{ fontSize: 13 }} />
                {t('language.select')}
              </Typography>
            </Box>
            {[{ code: 'es', label: 'Español', flag: '🇩🇴' }, { code: 'en', label: 'English', flag: '🇺🇸' }].map((lang) => (
              <MenuItem
                key={lang.code}
                onClick={() => { i18n.changeLanguage(lang.code); handleUserMenuClose(); }}
                selected={i18n.resolvedLanguage === lang.code}
                dense
              >
                <ListItemIcon sx={{ fontSize: '1rem', minWidth: 32 }}>{lang.flag}</ListItemIcon>
                <ListItemText>{lang.label}</ListItemText>
                {i18n.resolvedLanguage === lang.code && <Check fontSize="small" color="primary" />}
              </MenuItem>
            ))}

            <Divider />

            {/* Logout */}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" color="error" />
              </ListItemIcon>
              <Typography color="error">{t('auth.logout')}</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSuccess={handlePasswordChangeSuccess}
        isRequired={false}
      />
    </AppBar>
  );
};
